/**
 * Side drawer + table-drawer triggers for Specs / Size / What's in the Box
 * and accessory detail drawers.
 */
(function () {
  const trapFocusHandlers = {};
  const BODY_OPEN_CLASS = 'h-drawer-open';
  const TRANSITION_MS = 600;

  function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
    if (elementToFocus) elementToFocus.focus();
  }

  function trapFocus(container, elementToFocus = container) {
    const focusableEls = Array.from(
      container.querySelectorAll(
        'summary, a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
      )
    );
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];
    removeTrapFocus();
    trapFocusHandlers.focusin = (evt) => {
      if (evt.target === container || evt.target === lastEl || evt.target === firstEl) {
        document.addEventListener('keydown', trapFocusHandlers.keydown);
      }
    };
    trapFocusHandlers.focusout = () => {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };
    trapFocusHandlers.keydown = (evt) => {
      if (evt.code !== 'Tab') return;
      if (evt.target === lastEl && !evt.shiftKey) {
        evt.preventDefault();
        firstEl?.focus();
      }
      if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
        evt.preventDefault();
        lastEl?.focus();
      }
    };
    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);
    (elementToFocus || container).focus({ preventScroll: true });
  }

  function ensureOverlay() {
    let overlay = document.querySelector('.js-overlay.accessory-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'accessory-overlay js-overlay';
      document.body.appendChild(overlay);
    } else if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
    return overlay;
  }

  function setViewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }

  function lockScroll() {
    // IMPORTANT: do not lock the desktop document itself. The product gallery is
    // sticky and forcing html/body into a viewport-height overflow state can make
    // the left hero/media column disappear while a drawer is open.
    // Mobile still gets a true scroll lock where background scrolling is much
    // more disruptive.
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    if (document.body.classList.contains(BODY_OPEN_CLASS)) return;
    document.body.dataset.hDrawerScrollY = String(window.scrollY || 0);
    document.body.classList.add(BODY_OPEN_CLASS);
    document.documentElement.classList.add(BODY_OPEN_CLASS);
  }

  function unlockScroll() {
    if (document.querySelector('side-drawer[open]')) return;
    if (!document.body.classList.contains(BODY_OPEN_CLASS) && !document.documentElement.classList.contains(BODY_OPEN_CLASS)) return;
    const y = parseInt(document.body.dataset.hDrawerScrollY || '0', 10);
    document.body.classList.remove(BODY_OPEN_CLASS);
    document.documentElement.classList.remove(BODY_OPEN_CLASS);
    delete document.body.dataset.hDrawerScrollY;
    window.scrollTo(0, y);
  }

  function waitForDrawerTransition(drawer) {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        drawer.removeEventListener('transitionend', onEnd);
        resolve();
      };
      const onEnd = (evt) => {
        if (evt.target !== drawer) return;
        if (evt.propertyName !== 'transform') return;
        finish();
      };
      drawer.addEventListener('transitionend', onEnd);
      setTimeout(finish, TRANSITION_MS + 80);
    });
  }

  if (!customElements.get('side-drawer')) {
    class SideDrawer extends HTMLElement {
      connectedCallback() {
        // Always mount on body so fixed positioning is viewport-relative.
        // Keeping drawers inside sticky product__info leaks a vertical strip on the right.
        this.portalToBody();
        this.overlay = ensureOverlay();
        if (!this.hasAttribute('open')) {
          this.setAttribute('aria-hidden', 'true');
          this.setAttribute('inert', '');
        }
      }

      handleClick(evt) {
        if (evt.target.closest('.js-close-drawer') || evt.target === this.overlay) {
          this.close();
        }
      }

      portalToBody() {
        if (!document.body || this.parentElement === document.body) return;
        document.body.appendChild(this);
      }

      open(opener) {
        if (this.hasAttribute('open') || this._opening) return;

        document.querySelectorAll('side-drawer[open]').forEach((other) => {
          if (other !== this) other.close({ immediate: true });
        });

        this._opening = true;
        this.opener = opener || document.activeElement;
        this.opener?.setAttribute?.('aria-expanded', 'true');
        this.portalToBody();
        this.overlay = ensureOverlay();
        setViewportHeight();
        lockScroll();

        this.clickHandler = this.clickHandler || this.handleClick.bind(this);
        this.keyupHandler =
          this.keyupHandler ||
          ((evt) => {
            if (evt.key === 'Escape') this.close();
          });
        this.addEventListener('click', this.clickHandler);
        this.addEventListener('keyup', this.keyupHandler);

        // The backdrop is the primary outside-click target. Bind both pointerdown
        // and click so it also works inside the Shopify theme editor and on touch.
        this.overlayPointerHandler = this.overlayPointerHandler || ((evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          this.close();
        });
        this.overlayBlockScroll = this.overlayBlockScroll || ((evt) => {
          evt.preventDefault();
        });
        this.overlay.addEventListener('pointerdown', this.overlayPointerHandler);
        this.overlay.addEventListener('click', this.overlayPointerHandler);
        this.overlay.addEventListener('wheel', this.overlayBlockScroll, { passive: false });
        this.overlay.addEventListener('touchmove', this.overlayBlockScroll, { passive: false });

        // Paint off-screen first, then animate in (Concept-style two-phase open).
        this.removeAttribute('open');
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        void this.offsetWidth;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.overlay.classList.add('is-visible');
            this.setAttribute('open', '');
            this._opening = false;
            trapFocus(this, this.querySelector('.js-close-drawer') || this);
          });
        });
      }

      async close({ immediate = false } = {}) {
        if (!this.hasAttribute('open') && !this._opening) return;
        this._opening = false;

        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        this.opener?.setAttribute?.('aria-expanded', 'false');
        this.overlay?.classList.remove('is-visible');

        if (!immediate) {
          await waitForDrawerTransition(this);
        }

        this.setAttribute('inert', '');
        removeTrapFocus(this.opener);
        this.removeEventListener('click', this.clickHandler);
        this.removeEventListener('keyup', this.keyupHandler);
        if (this.overlay) {
          this.overlay.removeEventListener('pointerdown', this.overlayPointerHandler);
          this.overlay.removeEventListener('click', this.overlayPointerHandler);
          this.overlay.removeEventListener('wheel', this.overlayBlockScroll);
          this.overlay.removeEventListener('touchmove', this.overlayBlockScroll);
        }
        unlockScroll();
      }
    }
    customElements.define('side-drawer', SideDrawer);
  }


  // Belt-and-suspenders outside close: the shared backdrop normally receives
  // the click, but Shopify editor layers or app widgets can sit above it. This
  // capture-phase handler closes the drawer whenever the pointer begins outside
  // the actual panel, regardless of what page layer was clicked.
  if (!window.__hiknightDrawerOutsideCloseBound) {
    window.__hiknightDrawerOutsideCloseBound = true;
    const closeOpenDrawerFromOutside = (evt) => {
      const openDrawer = document.querySelector('side-drawer[open]');
      if (!openDrawer) return;
      if (openDrawer.contains(evt.target)) return;
      if (evt.target.closest?.('.js-table-drawer, .js-close-drawer')) return;
      openDrawer.close?.();
    };
    document.addEventListener('pointerdown', closeOpenDrawerFromOutside, true);
    document.addEventListener('mousedown', closeOpenDrawerFromOutside, true);
    document.addEventListener('touchstart', closeOpenDrawerFromOutside, { capture: true, passive: true });
  }

  if (!customElements.get('table-drawer')) {
    class TableDrawer extends HTMLElement {
      connectedCallback() {
        const trigger = this.querySelector('.js-table-drawer');
        if (!trigger || trigger.dataset.bound === 'true') return;
        trigger.dataset.bound = 'true';
        trigger.addEventListener('click', (evt) => {
          if (evt.target.closest('.variant-selector')) return;
          const btn = evt.target.closest('.js-table-drawer');
          if (!btn) return;
          const drawer = document.getElementById(`table-${btn.dataset.id}-drawer`);
          drawer?.open?.(btn);
        });
      }
    }
    customElements.define('table-drawer', TableDrawer);
  }

  // Robust fallback: bind HIKNIGHT spec/size/box side-drawer triggers by delegation.
  // This keeps the buttons working even if another snippet registers a custom element first.
  if (!window.__hiknightTableDrawerDelegationBound) {
    window.__hiknightTableDrawerDelegationBound = true;
    document.addEventListener('click', (evt) => {
      const btn = evt.target.closest('.js-table-drawer');
      if (!btn || evt.target.closest('.variant-selector')) return;
      const drawer = document.getElementById(`table-${btn.dataset.id}-drawer`);
      drawer?.open?.(btn);
    });
  }

  if (!customElements.get('accessories-bundle')) {
    class AccessoriesBundle extends HTMLElement {
      connectedCallback() {}
    }
    customElements.define('accessories-bundle', AccessoriesBundle);
  }
})();

/* HIKNIGHT V5 — Accessories / Free Gifts tab interaction. */
(function () {
  const TAB_SELECTOR = '[data-hk-offers-tab]';
  const ROOT_SELECTOR = '[data-hk-offers-tabs]';

  function activate(root, key, moveFocus) {
    if (!root) return;
    const tabs = Array.from(root.querySelectorAll(TAB_SELECTOR));
    const panels = Array.from(root.querySelectorAll('[data-hk-offers-panel]'));
    const nextTab = tabs.find((tab) => tab.dataset.hkOffersTab === key);
    const nextPanel = panels.find((panel) => panel.dataset.hkOffersPanel === key);
    if (!nextTab || !nextPanel) return;

    tabs.forEach((tab) => {
      const active = tab === nextTab;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });

    panels.forEach((panel) => {
      if (panel === nextPanel) return;
      panel.classList.remove('is-active');
      panel.hidden = true;
    });

    nextPanel.hidden = false;
    // Force a first frame so opacity/translate transitions remain visible.
    void nextPanel.offsetWidth;
    nextPanel.classList.add('is-active');
    if (moveFocus) nextTab.focus({ preventScroll: true });
  }

  function bind(root) {
    if (!root || root.dataset.hkOffersBound === 'true') return;
    root.dataset.hkOffersBound = 'true';

    root.addEventListener('click', (event) => {
      // Paid accessory cards are intentionally selectable across the full row.
      // Preserve native behavior for the checkbox itself and any future interactive
      // controls (links, selects, buttons, variant pickers) so we never double-toggle.
      const accessoryRow = event.target.closest('.accessory');
      if (accessoryRow && root.contains(accessoryRow)) {
        const interactive = event.target.closest('input, select, button, a, textarea, summary, details, [role=button], .variant-selector');
        if (!interactive) {
          const checkbox = accessoryRow.querySelector('.accessory-checkbox:not(:disabled)');
          if (checkbox) {
            event.preventDefault();
            checkbox.click();
          }
          return;
        }
      }

      const accessoriesMore = event.target.closest('[data-hk-accessories-more]');
      if (accessoriesMore && root.contains(accessoriesMore)) {
        const panel = accessoriesMore.closest('[data-hk-offers-panel="accessories"]');
        const rows = panel?.querySelector('[data-hk-accessory-rows]');
        if (!rows) return;
        const willExpand = rows.classList.contains('is-collapsed');
        rows.classList.toggle('is-collapsed', !willExpand);
        accessoriesMore.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
        const label = accessoriesMore.querySelector('[data-hk-accessories-more-label]');
        if (label) label.textContent = willExpand ? (accessoriesMore.dataset.expandedLabel || 'Show fewer accessories') : (accessoriesMore.dataset.collapsedLabel || 'Show more');
        return;
      }

      const giftsMore = event.target.closest('[data-hk-gifts-more]');
      if (giftsMore && root.contains(giftsMore)) {
        const panel = giftsMore.closest('[data-hk-offers-panel="gifts"]');
        const rows = panel?.querySelector('[data-hk-gift-rows]');
        if (!rows) return;
        const willExpand = rows.classList.contains('is-collapsed');
        rows.classList.toggle('is-collapsed', !willExpand);
        giftsMore.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
        const label = giftsMore.querySelector('[data-hk-gifts-more-label]');
        if (label) label.textContent = willExpand ? (giftsMore.dataset.expandedLabel || 'Show fewer gifts') : (giftsMore.dataset.collapsedLabel || 'Show more');
        return;
      }

      const tab = event.target.closest(TAB_SELECTOR);
      if (!tab || !root.contains(tab)) return;
      activate(root, tab.dataset.hkOffersTab, false);
    });

    root.addEventListener('keydown', (event) => {
      const tab = event.target.closest(TAB_SELECTOR);
      if (!tab || !root.contains(tab)) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const tabs = Array.from(root.querySelectorAll(TAB_SELECTOR));
      if (tabs.length < 2) return;
      event.preventDefault();
      let index = tabs.indexOf(tab);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
      else index = (index - 1 + tabs.length) % tabs.length;
      activate(root, tabs[index].dataset.hkOffersTab, true);
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(bind);
  }

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
